import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Play } from 'lucide-react';
import gsap from 'gsap';
import './RobotEyeHero.css';

export default function RobotEyeHero() {
  const navigate = useNavigate();
  const heroRef = useRef(null);

  /* ── GSAP entry animation ── */
  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.set(['.ch-badge', '.ch-title', '.ch-subtitle', '.ch-actions', '.ch-telemetry-hud'], {
        opacity: 0, y: 32,
      });
      const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });
      tl.to('.ch-badge',        { opacity: 1, y: 0, duration: 0.75, delay: 0.2 })
        .to('.ch-title',        { opacity: 1, y: 0, duration: 0.95 }, '-=0.5')
        .to('.ch-subtitle',     { opacity: 1, y: 0, duration: 0.8  }, '-=0.65')
        .to('.ch-actions',      { opacity: 1, y: 0, duration: 0.75 }, '-=0.6')
        .to('.ch-telemetry-hud',{ opacity: 1, y: 0, duration: 0.8  }, '-=0.5');
    }, heroRef);
    return () => ctx.revert();
  }, []);

  return (
    <section ref={heroRef} className="ch-hero" data-testid="hero-section">

      {/* ── Full-bleed right image ── */}
      <div className="ch-image-panel" aria-hidden="true">
        <img
          src="/hero_bg.jpg"
          alt="Drishti AI Trading Intelligence"
          className="ch-image"
          draggable="false"
        />
        <div className="ch-image-fade" />
      </div>

      {/* ── LEFT: Content ── */}
      <div className="ch-left-container">
        <div className="ch-left">

          {/* Badge */}
          <div className="ch-badge">
            <span className="ch-badge-dot" />
            AI-Powered Trading Intelligence
          </div>

          {/* Headline */}
          <h1 className="ch-title">
            See the <span className="ch-title-cyan">Market</span><br />
            Before It <span className="ch-title-emerald">Moves.</span>
          </h1>

          {/* Subtitle */}
          <p className="ch-subtitle">
            Drishti AI scans 500+ stocks in real-time, detects institutional moves,
            and alerts you before the crowd even notices.
          </p>

          {/* CTAs */}
          <div className="ch-actions">
            <button
              className="ch-btn-primary"
              onClick={() => navigate('/login')}
              data-testid="hero-primary-btn"
            >
              <span>Start Learning</span>
              <ArrowRight className="ch-btn-icon" />
            </button>
            <a
              href="https://super-trader.jamadhan.com"
              target="_blank"
              rel="noopener noreferrer"
              className="ch-btn-outline"
              data-testid="hero-secondary-btn"
            >
              <Play className="ch-btn-play" />
              <span>Join Workshop</span>
            </a>
          </div>

          {/* ── Institutional Telemetry HUD ── */}
          <div className="ch-telemetry-hud" data-testid="hero-stats-hud">

            <div className="ch-telemetry-item">
              <span className="ch-telemetry-val ch-val-amber">4.9★</span>
              <span className="ch-telemetry-label">Google rating (100 + reviews)</span>
            </div>

            <div className="ch-telemetry-divider" />

            <div className="ch-telemetry-item">
              <span className="ch-telemetry-val ch-val-emerald">10K+</span>
              <span className="ch-telemetry-label">Traders trained*</span>
            </div>

            <div className="ch-telemetry-divider" />

            <div className="ch-telemetry-item">
              <div className="ch-telemetry-live-row">
                <span className="ch-telemetry-live-dot" />
                <span className="ch-telemetry-val ch-val-cyan">Live</span>
              </div>
              <span className="ch-telemetry-label">Mentor-led classes</span>
            </div>

            <div className="ch-telemetry-divider" />

            <div className="ch-telemetry-item">
              <span className="ch-telemetry-val ch-val-white">2019</span>
              <span className="ch-telemetry-label">Trading since</span>
            </div>

          </div>

        </div>
      </div>

    </section>
  );
}
